import { writable, get } from "svelte/store";
import { goto } from "$app/navigation";
import { routes } from "$lib/routes";
import type { AuthStore } from "$lib/stores/authStore";

export interface HomepageStore {
  isInShortcutMode: boolean;
  currentShortcutSequence: string;
}

export const homepageStore = writable<HomepageStore>({
  isInShortcutMode: false,
  currentShortcutSequence: "",
});

export const setShortcutMode = (isEnabled: boolean) => {
  homepageStore.update((state) => ({
    ...state,
    isInShortcutMode: isEnabled,
    currentShortcutSequence: "",
  }));
};

export const setShortcutSequence = (sequence: string) => {
  homepageStore.update((state) => ({
    ...state,
    currentShortcutSequence: sequence,
  }));
};

export const clearShortcutSequence = () => {
  homepageStore.update((state) => ({
    ...state,
    currentShortcutSequence: "",
  }));
};

// Reset shortcut state
export const resetShortcutState = () => {
  homepageStore.update((state) => ({
    ...state,
    isInShortcutMode: false,
    currentShortcutSequence: "",
  }));

  document.querySelectorAll('[id$="-dropdown"]').forEach((dropdown) => {
    if (dropdown instanceof HTMLElement) {
      dropdown.style.display = "none";
    }
  });
};

// Create a type for shortcut definitions
export interface ShortcutHandler {
  id: string;
  sequence: string;
  description: string;
  execute: () => void;
  isEnabled: () => boolean;
}

export const createHomepageShortcuts = (authStore: AuthStore) => {
  const shortcuts: ShortcutHandler[] = [
    {
      id: "escape-shortcut-mode",
      sequence: "escape",
      description: "Exit shortcut mode",
      execute: () => {
        setShortcutMode(false);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-login",
      sequence: "l",
      description: "Go to Sign In",
      execute: () => {
        logger.log("Navigating to sign in");
        goto(routes.signIn);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-signup",
      sequence: "s",
      description: "Go to Sign Up",
      execute: () => {
        logger.log("Navigating to sign up");
        goto(routes.signUp);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-home",
      sequence: "h",
      description: "Go to Homepage",
      execute: () => {
        goto(routes.root);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-blog",
      sequence: "b",
      description: "Go to Blog",
      execute: () => {
        goto(routes.blog);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-pricing",
      sequence: "p",
      description: "Go to Pricing",
      execute: () => {
        goto(routes.pricing);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    // Features dropdown shortcuts
    {
      id: "open-features-dropdown",
      sequence: "f",
      description: "Open Features menu",
      execute: () => {
        setShortcutSequence("f");
      },
      isEnabled: () => true,
    },
    {
      id: "goto-features-ai-web-scraping",
      sequence: "f1",
      description: "Go to Features / AI Web Scraping",
      execute: () => {
        goto("/features/ai-web-scraping");
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-features-ai-pdf-processing",
      sequence: "f2",
      description: "Go to Features / AI Pdf Processing",
      execute: () => {
        goto("/features/ai-pdf-processing");
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    // Use Cases dropdown shortcuts
    {
      id: "open-usecases-dropdown",
      sequence: "u",
      description: "Open Use Cases menu",
      execute: () => {
        setShortcutSequence("u");
      },
      isEnabled: () => true,
    },
    {
      id: "goto-ecommerce-competitor-research",
      sequence: "u1",
      description: "Go to E-commerce Competitor Research",
      execute: () => {
        goto("/use-cases/ai-ecommerce-automation");
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-ai-resume-screening",
      sequence: "u2",
      description: "Go to AI Resume Screening",
      execute: () => {
        goto("/use-cases/recruiting-ai-resume-screening");
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    // Company dropdown shortcuts
    {
      id: "open-company-dropdown",
      sequence: "c",
      description: "Open Company menu",
      execute: () => {
        setShortcutSequence("c");
      },
      isEnabled: () => true,
    },
    {
      id: "goto-about",
      sequence: "c1",
      description: "Go to About",
      execute: () => {
        goto("/about");
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    {
      id: "goto-blog-from-company",
      sequence: "c2",
      description: "Go to Blog",
      execute: () => {
        goto(routes.blog);
        resetShortcutState();
      },
      isEnabled: () => true,
    },
    // Form field shortcuts
    {
      id: "focus-email",
      sequence: "e",
      description: "Focus email input",
      execute: () => {
        document.getElementById("email")?.focus();
        resetShortcutState();
      },
      isEnabled: () => window.location.pathname === routes.signIn || window.location.pathname === routes.signUp,
    },
    {
      id: "focus-password",
      sequence: "w",
      description: "Focus password input",
      execute: () => {
        document.getElementById("password")?.focus();
        resetShortcutState();
      },
      isEnabled: () => window.location.pathname === routes.signIn || window.location.pathname === routes.signUp,
    },
    {
      id: "focus-password-confirm",
      sequence: "d",
      description: "Focus confirm password input",
      execute: () => {
        document.getElementById("passwordConfirmation")?.focus();
        resetShortcutState();
      },
      isEnabled: () => window.location.pathname === routes.signUp,
    },
    {
      id: "google-signin",
      sequence: "g",
      description: "Sign in with Google",
      execute: () => {
        const handleGoogleSignin = () => {
          authStore.initiateGoogleSignin();
        };
        handleGoogleSignin();
        resetShortcutState();
      },
      isEnabled: () => window.location.pathname === routes.signIn || window.location.pathname === routes.signUp,
    },
    {
      id: "submit-form",
      sequence: "enter",
      description: "Submit form",
      execute: () => {
        const submitBtn = document.querySelector('form button[type="submit"]');
        if (submitBtn) {
          (submitBtn as HTMLButtonElement).click();
        }
        resetShortcutState();
      },
      isEnabled: () => window.location.pathname === routes.signIn || window.location.pathname === routes.signUp,
    },
  ];

  const getHandler = (sequence: string): ShortcutHandler | undefined => {
    return shortcuts.find((shortcut) => shortcut.sequence === sequence && shortcut.isEnabled());
  };

  const isPartialMatch = (sequence: string): boolean => {
    return shortcuts.some((shortcut) => shortcut.sequence.startsWith(sequence) && shortcut.isEnabled());
  };

  const getMatchingShortcuts = (sequence: string): ShortcutHandler[] => {
    return shortcuts.filter(
      (shortcut) => shortcut.sequence.startsWith(sequence) && shortcut.sequence !== sequence && shortcut.isEnabled(),
    );
  };

  const handleShortcutKey = (key: string): boolean => {
    const currentSequence = get(homepageStore).currentShortcutSequence;
    const newSequence = currentSequence + key;

    // Check if this is a complete sequence
    const exactHandler = getHandler(newSequence);
    if (exactHandler) {
      exactHandler.execute();
      return true;
    }

    // Check if this could be part of a longer sequence
    if (isPartialMatch(newSequence)) {
      setShortcutSequence(newSequence);
      return true;
    }

    // If no match and no potential match, reset
    resetShortcutState();
    return false;
  };

  return {
    shortcuts,
    getHandler,
    isPartialMatch,
    getMatchingShortcuts,
    handleShortcutKey,
  };
};
